import { useQuery } from '@tanstack/react-query';
import { Link, useRouterState } from '@tanstack/react-router';
import {
  ChartColumnIcon,
  ChevronDownIcon,
  LayoutDashboardIcon,
  ListIcon,
} from 'lucide-react';
import { useEffect, useState, type JSX } from 'react';

import { Button } from '@/components/ui/button.js';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip.js';
import { isOverviewReport } from '@/features/code-quality/report-origin.js';
import { useHintTarget } from '@/features/onboarding/useHintTarget.js';
import { useUiStore } from '@/features/settings/ui-store.js';
import {
  dashboardsQueryOptions,
  type SavedDashboard,
} from '@/lib/api/dashboards.js';
import { getRuntimeConfig } from '@/lib/runtime-config.js';
import { cn } from '@/lib/utils.js';

import {
  sidebarIconClass,
  sidebarRowActionClass,
  sidebarRowActive,
  sidebarRowBase,
  sidebarRowHover,
} from '../../app/sidebar-primitives.js';

interface SidebarDashboardsProps {
  collapsed?: boolean;
  onNavigate?: () => void;
  /** Whether the selected workspace is the reader's personal one, where the Code Quality node shows. */
  isPersonal?: boolean;
}

/** The address of the cross-workspace Code Quality overview. */
const CODE_QUALITY_PATH = '/dashboards/code-quality';

/**
 * Sidebar "Dashboards" section, built like "Scheduled": the title expands an inline tree with one node
 * per saved dashboard of the selected workspace, in the order the Dashboards page sets, and a trailing
 * list icon opens that full page. The tree starts expanded while a dashboard's own page is open, so its
 * node is visible and marked. In the personal workspace, while the fail-closed Code Quality flag is on,
 * a fixed Code Quality node comes first: the overview of every project the reader may read. It stays
 * marked, with the tree open, on a report opened from that overview.
 */
export function SidebarDashboards({
  collapsed = false,
  onNavigate,
  isPersonal = false,
}: SidebarDashboardsProps): JSX.Element {
  const showCodeQuality =
    isPersonal && getRuntimeConfig().features.codeQuality === true;
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const onOverviewReport = useRouterState({
    select: (state) => isOverviewReport(state.location),
  });
  const onDashboardPage =
    pathname.startsWith('/dashboards/') || onOverviewReport;
  const [open, setOpen] = useState(onDashboardPage);
  const selectedWorkspaceId = useUiStore((state) => state.selectedWorkspaceId);
  /**
   * `isPending` so a list still waiting for the workspace shows skeletons, and `isError` so a failed
   * load says so — neither may read as "No dashboards yet".
   */
  const { data, isPending, isError } = useQuery(
    dashboardsQueryOptions(selectedWorkspaceId),
  );
  const dashboards = data ?? [];
  /**
   * The sidebar stays mounted across routes, so the initial state alone would leave the tree closed
   * when the app moves onto a dashboard's page later (history back, a link elsewhere). Open it on
   * entry only: a manual collapse on that page stands until the user leaves and comes back.
   */
  useEffect(() => {
    if (onDashboardPage) setOpen(true);
  }, [onDashboardPage]);
  /** One id, two mount points — only the branch matching the rail state is ever in the tree. */
  const dashboardsHintRef = useHintTarget<HTMLElement>(
    'nav.dashboards',
    'gate',
  );

  if (collapsed) {
    return (
      <div className="px-3">
        <Link
          ref={dashboardsHintRef}
          to="/dashboards"
          onClick={onNavigate}
          className={cn(sidebarRowBase, sidebarRowHover, 'w-full')}
          activeProps={{ className: sidebarRowActive }}
        >
          <LayoutDashboardIcon className={sidebarIconClass} />
          <span className="sidebar-label sidebar-fade truncate text-left">
            Dashboards
          </span>
        </Link>
      </div>
    );
  }

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="sidebar-fade min-w-0 px-3"
    >
      <div
        data-hint-beacon-row
        className={cn(sidebarRowBase, sidebarRowHover, 'group/dashhead')}
      >
        <CollapsibleTrigger asChild>
          <button
            ref={dashboardsHintRef}
            type="button"
            className="group flex min-w-0 flex-1 items-center gap-1.5 max-md:gap-3 cursor-pointer text-inherit outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-foreground/20 rounded-[6px]"
          >
            <LayoutDashboardIcon className={sidebarIconClass} />
            <span className="sidebar-label sidebar-fade truncate text-left">
              Dashboards
            </span>
            <ChevronDownIcon
              data-chevron
              className={cn(
                sidebarIconClass,
                'transition-transform duration-200 ease-out',
                open ? 'rotate-0' : '-rotate-90',
              )}
            />
          </button>
        </CollapsibleTrigger>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              asChild
              variant="ghost"
              size="icon-sm"
              aria-label="All dashboards"
              className={cn(
                sidebarRowActionClass,
                'md:group-hover/dashhead:opacity-100 focus-visible:opacity-100',
              )}
            >
              <Link to="/dashboards" onClick={onNavigate}>
                <ListIcon className="size-4" strokeWidth={1.75} aria-hidden />
              </Link>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">All dashboards</TooltipContent>
        </Tooltip>
      </div>

      <CollapsibleContent className="flex flex-col gap-0.5 pt-0.5 pl-3">
        {showCodeQuality && (
          <CodeQualityNode
            active={pathname === CODE_QUALITY_PATH || onOverviewReport}
            onNavigate={onNavigate}
          />
        )}
        {isPending && (
          <div className="flex flex-col gap-[6px] px-[10px] py-1">
            <Skeleton className="h-8 w-full rounded-[6px]" />
            <Skeleton className="h-8 w-3/4 rounded-[6px]" />
          </div>
        )}
        {isError && (
          <p className="sidebar-fade px-[10px] pb-2 text-xs text-sidebar-section/70">
            Could not load dashboards
          </p>
        )}
        {!isPending &&
          !isError &&
          dashboards.length === 0 &&
          !showCodeQuality && (
            <p className="sidebar-fade px-[10px] pb-2 text-xs text-sidebar-section/70">
              No dashboards yet
            </p>
          )}
        {dashboards.map((dashboard) => (
          <DashboardNode
            key={dashboard.id}
            dashboard={dashboard}
            active={pathname === `/dashboards/${dashboard.id}`}
            onNavigate={onNavigate}
          />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}

/**
 * The fixed node of the cross-workspace Code Quality overview. It has the icon of a dashboard, so it
 * reads apart from the gauge of the workspace's own Code Quality row.
 */
function CodeQualityNode({
  active,
  onNavigate,
}: {
  active: boolean;
  onNavigate?: () => void;
}): JSX.Element {
  return (
    <Link
      to={CODE_QUALITY_PATH}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      className={cn(
        sidebarRowBase,
        active ? sidebarRowActive : sidebarRowHover,
        'w-full',
      )}
    >
      <ChartColumnIcon className={sidebarIconClass} strokeWidth={1.75} />
      <span className="flex-1 truncate text-inherit">Code Quality</span>
    </Link>
  );
}

/** One dashboard in the tree; opens its own page. */
function DashboardNode({
  dashboard,
  active,
  onNavigate,
}: {
  dashboard: SavedDashboard;
  active: boolean;
  onNavigate?: () => void;
}): JSX.Element {
  const name = dashboard.name.trim() || 'Untitled';
  return (
    <Link
      to="/dashboards/$id"
      params={{ id: dashboard.id }}
      onClick={onNavigate}
      title={name}
      aria-current={active ? 'page' : undefined}
      className={cn(
        sidebarRowBase,
        active ? sidebarRowActive : sidebarRowHover,
        'w-full',
      )}
    >
      <ChartColumnIcon className={sidebarIconClass} strokeWidth={1.75} />
      <span className="flex-1 truncate text-inherit">{name}</span>
    </Link>
  );
}
